import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Combine, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import useDebouncedValue from "@/hooks/useDebouncedValue";

function nameOf(r) {
  return r?.name || `${r?.first_name || ""} ${r?.last_name || ""}`.trim() || "—";
}

/**
 * Merge an imported/flagged (Review-queue) realtor INTO an existing one.
 * Referrals, timeline touches, follows and harvested contacts move onto the
 * kept record; the source is soft-deleted. Surfaces the ISN-suggested match
 * first when present.
 */
export default function MergeRealtorDialog({ source, open, onClose, onMerged }) {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [mergingId, setMergingId] = useState("");

  useEffect(() => {
    if (!open || !source) return;
    setSearch("");
    setRows([]);
    setSuggested(null);
    const sid = source.isn_suggested_realtor_id;
    if (sid) {
      api.get(`/realtors/${sid}`).then(({ data }) => setSuggested(data)).catch(() => {});
    }
  }, [open, source]);

  useEffect(() => {
    if (!open) return;
    const q = debounced.trim();
    if (!q) { setRows([]); return; }
    setLoading(true);
    api.get("/realtors/paged", { params: { search: q, limit: 8, sort: "name:asc" } })
      .then(({ data }) => setRows((data.rows || []).filter((r) => r.id !== source?.id)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [debounced, open, source?.id]);

  const doMerge = async (target) => {
    if (!source || !target) return;
    setMergingId(target.id);
    try {
      const { data } = await api.post(`/realtors/${source.id}/merge-into`, { target_id: target.id });
      toast.success("Merged", { description: `${nameOf(source)} → ${nameOf(target)}` });
      onMerged?.(source.id, data.target);
      onClose();
    } catch (e) {
      toast.error("Merge failed", { description: formatApiError(e) });
    } finally {
      setMergingId("");
    }
  };

  const Row = ({ r, isSuggested }) => (
    <div
      className={`flex items-center justify-between gap-2 rounded-md border p-2.5 ${isSuggested ? "border-teal-300 bg-teal-50/50" : "border-slate-200"}`}
      data-testid={`merge-candidate-${r.id}`}
    >
      <div className="min-w-0">
        <div className="font-medium text-slate-900 text-sm truncate flex items-center gap-1.5">
          {isSuggested ? <Sparkles className="h-3.5 w-3.5 text-teal-600 shrink-0" /> : null}
          {nameOf(r)}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {r.brokerage_name || "No brokerage"}{r.phone ? ` · ${r.phone}` : ""}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => doMerge(r)}
        disabled={!!mergingId}
        className="shrink-0 h-8 text-white"
        style={{ background: "hsl(var(--brand))" }}
        data-testid={`merge-into-${r.id}`}
      >
        {mergingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Combine className="h-3.5 w-3.5 mr-1" />}
        Merge
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="merge-realtor-dialog">
        <DialogHeader>
          <DialogTitle>Merge {source ? nameOf(source) : "realtor"} into…</DialogTitle>
          <DialogDescription>
            Pick the existing realtor to keep. This imported record's referral, timeline touches and
            harvested contacts move onto the kept record, then this one is removed.
          </DialogDescription>
        </DialogHeader>

        {suggested ? (
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase tracking-wide text-teal-700 font-semibold">Suggested match</div>
            <Row r={suggested} isSuggested />
          </div>
        ) : null}

        <div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search realtors by name, brokerage, phone…"
              className="pl-8 min-h-[40px]"
              data-testid="merge-search-input"
              autoFocus
            />
          </div>
          <div className="mt-2 space-y-1.5 max-h-[40vh] overflow-y-auto">
            {loading ? (
              <div className="py-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
              </div>
            ) : rows.length === 0 && debounced.trim() ? (
              <div className="py-4 text-center text-sm text-slate-400">No matches.</div>
            ) : (
              rows.map((r) => <Row key={r.id} r={r} />)
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
